/**
 * UnifiedStreakWidget — Tier 4 P8.
 *
 * Surfaces the single AWENI flame computed across Memoriz / Games /
 * BalanceTab / Yawmi / SelectO. Replaces 4 competing per-app streaks
 * with one family-friendly metric.
 *
 * Render strategy (mobile-first, fail-silent):
 *   1. Synchronous render with localStorage cache (LCP-safe).
 *   2. Background fetch /v1/streak for fresh state.
 *   3. Optional /v1/streak/sync once per session to fold per-app counts.
 *   4. Guest users see a sign-in CTA, never a broken card.
 */

import type { Widget, WidgetContext } from '../types';

interface ContributionDay {
  score?: number;
  memoriz?: number;
  games?: number;
  balancetab?: number;
  yawmi?: number;
  selecto?: number;
}

interface StreakState {
  current_length: number;
  longest_length: number;
  last_active_date: string | null;
  freeze_tokens: number;
  last_freeze_used: string | null;
  contributions_30d: Record<string, ContributionDay>;
  today_score: number;
  today_modules: string[];
  status: 'idle' | 'active' | 'at_risk' | 'broken';
  updated_at: string | null;
}

const LS_CACHE_KEY = 'aweni_unified_streak_v1';
const LS_SYNC_KEY = 'aweni_unified_streak_synced_at';
const SYNC_DEBOUNCE_MS = 5 * 60 * 1000; // 5 minutes between /sync calls

function loadCached(): StreakState | null {
  try {
    const raw = localStorage.getItem(LS_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StreakState;
    if (typeof parsed?.current_length !== 'number') return null;
    return parsed;
  } catch {
    return null;
  }
}

function saveCached(state: StreakState): void {
  try {
    localStorage.setItem(LS_CACHE_KEY, JSON.stringify(state));
  } catch {
    /* ignore */
  }
}

async function fetchStreak(): Promise<StreakState | null> {
  try {
    const resp = await fetch('/aweni/api/v1/streak', {
      cache: 'no-store',
      credentials: 'include',
    });
    if (!resp.ok) return null;
    return (await resp.json()) as StreakState;
  } catch {
    return null;
  }
}

async function syncStreakIfDue(): Promise<StreakState | null> {
  try {
    const last = Number(localStorage.getItem(LS_SYNC_KEY) || '0');
    if (Date.now() - last < SYNC_DEBOUNCE_MS) return null;
    const resp = await fetch('/aweni/api/v1/streak/sync', {
      method: 'POST',
      cache: 'no-store',
      credentials: 'include',
    });
    if (!resp.ok) return null;
    localStorage.setItem(LS_SYNC_KEY, String(Date.now()));
    return (await resp.json()) as StreakState;
  } catch {
    return null;
  }
}

function isoToday(): string {
  return new Date().toISOString().slice(0, 10);
}

function dayMinus(daysAgo: number): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - daysAgo);
  return d.toISOString().slice(0, 10);
}

function shortDayLabel(iso: string, locale: string): string {
  try {
    return new Intl.DateTimeFormat(locale, { weekday: 'narrow' }).format(new Date(iso + 'T12:00:00Z'));
  } catch {
    return iso.slice(8, 10);
  }
}

function renderStrip(contributions: Record<string, ContributionDay>, locale: string): string {
  const today = isoToday();
  const days: { iso: string; label: string; isToday: boolean; score: number }[] = [];
  for (let i = 6; i >= 0; i--) {
    const iso = dayMinus(i);
    const day = contributions[iso] || {};
    days.push({
      iso,
      label: shortDayLabel(iso, locale),
      isToday: iso === today,
      score: Number(day.score || 0),
    });
  }
  return `
    <div class="grid grid-cols-7 gap-1.5 mt-3">
      ${days.map((d) => {
        const filled = d.score >= 1;
        const intensity = Math.min(1, d.score / 3);
        const bg = filled
          ? `background:rgba(245,158,11,${0.35 + 0.55 * intensity});`
          : 'background:var(--bg-secondary);';
        const ring = d.isToday ? 'box-shadow:0 0 0 2px var(--accent);' : '';
        return `
          <div class="flex flex-col items-center gap-1">
            <div class="rounded-full" style="width:18px;height:18px;${bg}${ring}"></div>
            <span class="text-[10px]" style="color:var(--text-secondary);font-feature-settings:'tnum';">${d.label}</span>
          </div>
        `;
      }).join('')}
    </div>
  `;
}

function renderModuleChips(modules: string[], t: WidgetContext['t']): string {
  if (!modules.length) return '';
  const labels: Record<string, string> = {
    memoriz: t('streak_module_memoriz') || '🕋 Memoriz',
    games: t('streak_module_games') || '🎮 Games',
    balancetab: t('streak_module_balancetab') || '💰 Tab',
    yawmi: t('streak_module_yawmi') || '🌙 Yawmi',
    selecto: t('streak_module_selecto') || '🍽 SelectO',
  };
  const chips = modules
    .map((m) => labels[m] || m)
    .map(
      (label) => `
        <span class="text-[11px] px-2 py-0.5 rounded-full" style="background:rgba(245,158,11,0.12);color:#9C8348;border:1px solid rgba(245,158,11,0.25);">${label}</span>
      `,
    )
    .join('');
  return `<div class="flex flex-wrap gap-1.5 mt-2">${chips}</div>`;
}

function renderState(container: HTMLElement, state: StreakState, ctx: WidgetContext): void {
  const { current_length, longest_length, freeze_tokens, contributions_30d, today_modules, status } = state;
  const flameClass = status === 'active' ? 'home-pulse-fire' : '';
  const flameOpacity = status === 'broken' || status === 'idle' ? '0.4' : '1';

  const subtitle =
    status === 'active'
      ? ctx.t('streak_today_kept') || 'Today logged · keep going!'
      : status === 'at_risk'
      ? ctx.t('streak_at_risk') || "Today not logged yet — don't break your streak"
      : status === 'broken'
      ? ctx.t('streak_broken') || 'Restart today — first day counts'
      : ctx.t('streak_idle') || 'Start your first streak today';

  const longestLine =
    longest_length > 0
      ? `<span class="text-[11px]" style="color:var(--text-secondary);">${ctx.t('streak_longest')?.replace('{n}', String(longest_length)) || `Longest · ${longest_length}d`}</span>`
      : '';

  const tokensLine =
    freeze_tokens > 0
      ? `<span class="text-[11px] px-2 py-0.5 rounded-full" style="background:#3B82F61A;color:#3B82F6;">❄ ${freeze_tokens} ${ctx.t('streak_freeze_token') || 'freeze'}</span>`
      : '';

  container.innerHTML = `
    <div class="rounded-2xl p-4 cursor-pointer" style="background:var(--card-bg);box-shadow:var(--shadow-sm);" data-streak-card>
      <div class="flex items-center justify-between gap-3">
        <div class="flex items-center gap-3">
          <span class="${flameClass}" style="font-size:32px;opacity:${flameOpacity};">🔥</span>
          <div>
            <div class="flex items-baseline gap-1">
              <span class="text-3xl font-bold" style="color:var(--text-primary);font-feature-settings:'tnum';">${current_length}</span>
              <span class="text-sm" style="color:var(--text-secondary);">${current_length === 1 ? (ctx.t('streak_day') || 'day') : (ctx.t('streak_days') || 'days')}</span>
            </div>
            <div class="text-xs mt-0.5" style="color:var(--text-secondary);">${subtitle}</div>
          </div>
        </div>
        <div class="flex flex-col items-end gap-1">
          ${tokensLine}
          ${longestLine}
        </div>
      </div>
      ${renderStrip(contributions_30d || {}, ctx.locale)}
      ${renderModuleChips(today_modules || [], ctx.t)}
    </div>
  `;

  container.querySelector<HTMLElement>('[data-streak-card]')?.addEventListener('click', () => {
    const next = today_modules?.[0] === 'memoriz'
      ? 'games'
      : today_modules?.[0] === 'games'
      ? 'memoriz'
      : 'memoriz';
    ctx.navigate(next);
  });
}

function renderGuest(container: HTMLElement, ctx: WidgetContext): void {
  container.innerHTML = `
    <div class="rounded-2xl p-4" style="background:var(--card-bg);box-shadow:var(--shadow-sm);">
      <div class="flex items-center gap-3">
        <span style="font-size:28px;opacity:0.5;">🔥</span>
        <div class="flex-1">
          <div class="text-sm font-semibold" style="color:var(--text-primary);">${ctx.t('streak_guest_title') || 'Build a daily flame'}</div>
          <div class="text-xs mt-0.5" style="color:var(--text-secondary);">${ctx.t('streak_guest_desc') || 'Sign in to track your streak across all apps'}</div>
        </div>
        <button class="text-xs font-medium px-3 py-1.5 rounded-full" style="background:var(--accent);color:var(--accent-text);" data-nav-login>
          ${ctx.t('action_sign_in') || 'Sign in'}
        </button>
      </div>
    </div>
  `;
  container.querySelector<HTMLElement>('[data-nav-login]')?.addEventListener('click', () => {
    ctx.navigate('login');
  });
}

export const UnifiedStreakWidget: Widget = {
  id: 'unified-streak',
  label: {
    en: 'Unified Streak',
    fr: 'Série unifiée',
    ar: 'سلسلة موحدة',
    es: 'Racha unificada',
    de: 'Vereinheitlichte Serie',
  },
  icon: '🔥',
  defaultVisible: true,
  defaultOrder: 6,
  defaultSize: 'expanded',

  render(container: HTMLElement, _size: 'compact' | 'expanded', ctx: WidgetContext): void {
    if (ctx.isGuest) {
      renderGuest(container, ctx);
      return;
    }

    const cached = loadCached();
    if (cached) {
      renderState(container, cached, ctx);
    } else {
      // First-paint placeholder (still synchronous, no LCP regression).
      container.innerHTML = `
        <div class="rounded-2xl p-4" style="background:var(--card-bg);box-shadow:var(--shadow-sm);">
          <div class="flex items-center gap-3">
            <span style="font-size:32px;opacity:0.4;">🔥</span>
            <div>
              <div class="flex items-baseline gap-1">
                <span class="text-3xl font-bold" style="color:var(--text-primary);">0</span>
                <span class="text-sm" style="color:var(--text-secondary);">${ctx.t('streak_days') || 'days'}</span>
              </div>
              <div class="text-xs mt-0.5" style="color:var(--text-secondary);">${ctx.t('streak_idle') || 'Start your first streak today'}</div>
            </div>
          </div>
        </div>
      `;
    }

    // Background refresh (best-effort, fail-silent).
    queueMicrotask(async () => {
      const fresh = (await syncStreakIfDue()) || (await fetchStreak());
      if (fresh) {
        saveCached(fresh);
        if (container.isConnected) renderState(container, fresh, ctx);
      }
    });
  },
};
